import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Calendar, AlertTriangle, ExternalLink, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { DueDatePickerField } from "./DueDatePickerField";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Status validasi per baris (tampilan) — keputusan akhir tetap di backend.
const validate = (r, { checked, dd, stale }) => {
  if (stale) return { ok: false, msg: "Data invoice berubah — muat ulang" };
  if (!r.can_fill) return { ok: false, msg: "Lengkapi tanggal invoice dulu" };
  if (!checked) return { ok: false, msg: "Belum dipilih", idle: true };
  if (!dd) return { ok: false, msg: "Pilih due date baru", idle: true };
  if (!DATE_RE.test(dd)) return { ok: false, msg: "Format tanggal tidak valid" };
  if (r.invoice_date && dd < r.invoice_date) return { ok: false, msg: `Tidak boleh sebelum tanggal invoice (${fmtDate(r.invoice_date)})` };
  return { ok: true, msg: "Siap disimpan" };
};

const woLabel = (r) => [r.wo_number && `WO ${r.wo_number}`, r.variation_order_name && `VO ${r.variation_order_name}`].filter(Boolean).join(" · ");

const QueueRow = ({ r, st, checked, dd, note, err, onCheck, onDate, onNote, onOpenDetail }) => {
  const v = err ? { ok: false, msg: err } : st;
  return (
    <tr className="border-b border-slate-100 align-top hover:bg-slate-50" data-testid={`bulk-fill-row-${r.entry_id}`}>
      <td className="px-2 py-2">
        <input type="checkbox" checked={checked} disabled={!r.can_fill} onChange={(e) => onCheck(e.target.checked)} data-testid={`bulk-fill-check-${r.entry_id}`} />
      </td>
      <td className="px-2 py-2">
        <div className="text-[11px] font-semibold text-slate-800">{r.client || "—"}</div>
        <div className="text-[10px] text-slate-600">{r.project_label || "—"}</div>
        {woLabel(r) && <div className="text-[10px] text-slate-500">{woLabel(r)}</div>}
      </td>
      <td className="px-2 py-2">
        <div className={`font-semibold ${r.invoice_number ? "text-slate-800" : "text-amber-700 italic"}`}>{r.invoice_label}</div>
        {r.external_reference && <div className="text-[10px] text-slate-500">Ref: {r.external_reference}</div>}
        <button type="button" onClick={() => onOpenDetail(r)} className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-sky-700 hover:underline" data-testid={`bulk-fill-detail-${r.entry_id}`}>
          <ExternalLink size={10} /> Buka detail
        </button>
      </td>
      <td className="px-2 py-2 text-[11px]">{r.invoice_date ? fmtDate(r.invoice_date) : <span className="text-rose-700">Belum diisi</span>}</td>
      <td className="px-2 py-2 text-right tabular-nums text-[11px] font-semibold" data-testid={`bulk-fill-outstanding-${r.entry_id}`}>{formatIDR(r.outstanding)}</td>
      <td className="px-2 py-2 text-[10px]">
        {r.recommended_due_date ? (
          <div>
            <button type="button" disabled={!checked} onClick={() => onDate(r.recommended_due_date)} className="px-1.5 py-0.5 border border-emerald-400 text-emerald-800 disabled:opacity-50" data-testid={`bulk-fill-suggest-${r.entry_id}`}>
              Pakai saran {fmtDate(r.recommended_due_date)}
            </button>
            <div className="text-slate-500 mt-0.5">{r.recommendation_basis}</div>
          </div>
        ) : (
          <span className="text-slate-500 italic" data-testid={`bulk-fill-no-terms-${r.entry_id}`}>{r.recommendation_note}</span>
        )}
      </td>
      <td className="px-2 py-2 text-[11px] text-slate-500 italic" data-testid={`bulk-fill-current-${r.entry_id}`}>{r.current_due_date ? fmtDate(r.current_due_date) : "Belum diisi"}</td>
      <td className="px-2 py-2">
        <DueDatePickerField value={dd} onChange={onDate} disabled={!checked} minDate={r.invoice_date || undefined}
          testId={`bulk-fill-date-${r.entry_id}`} />
        <input type="text" value={note} disabled={!checked} onChange={(e) => onNote(e.target.value)} placeholder="Catatan FAT (opsional)"
          className="mt-1 block border border-slate-300 py-1 px-2 text-[11px] w-36 disabled:bg-slate-100" data-testid={`bulk-fill-note-${r.entry_id}`} />
      </td>
      <td className="px-2 py-2 text-[10px] max-w-[140px]" data-testid={`bulk-fill-status-${r.entry_id}`}>
        <span className={`inline-flex items-start gap-1 ${v.ok ? "text-emerald-700" : v.idle ? "text-slate-400" : "text-rose-700"}`}>
          {v.ok ? <CheckCircle2 size={11} className="shrink-0" /> : !v.idle && <AlertTriangle size={11} className="shrink-0" />} {v.msg}
        </span>
      </td>
    </tr>
  );
};

const ReviewPanel = ({ list, saving, onBack, onConfirm }) => (
  <div className="flex-1 overflow-auto p-4" data-testid="bulk-fill-review">
    <div className="text-xs font-semibold text-slate-700 mb-2">Pratinjau perubahan ({list.length} invoice) — hanya due date yang diubah. Belum ada data yang tersimpan.</div>
    <table className="w-full text-xs border border-slate-200">
      <thead className="bg-slate-50"><tr className="text-left">
        <th className="px-2 py-1">Invoice</th><th className="px-2 py-1">Tgl Invoice</th><th className="px-2 py-1 text-right">Outstanding</th>
        <th className="px-2 py-1">Due Date Lama</th><th className="px-2 py-1">Due Date Baru</th><th className="px-2 py-1">Catatan FAT</th>
      </tr></thead>
      <tbody>
        {list.map(({ r, dd, note }) => (
          <tr key={r.entry_id} className="border-t border-slate-100" data-testid={`bulk-fill-review-row-${r.entry_id}`}>
            <td className="px-2 py-1">{r.invoice_label}<div className="text-[10px] text-slate-500">{r.client} · {r.project_label}</div></td>
            <td className="px-2 py-1">{fmtDate(r.invoice_date)}</td>
            <td className="px-2 py-1 text-right tabular-nums">{formatIDR(r.outstanding)}</td>
            <td className="px-2 py-1 text-slate-500 italic">Belum diisi</td>
            <td className="px-2 py-1 font-semibold text-emerald-800"><span className="inline-flex items-center gap-1"><ArrowRight size={11} /> {fmtDate(dd)}</span></td>
            <td className="px-2 py-1 text-slate-600">{note || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="mt-3 flex gap-2 justify-end">
      <button onClick={onBack} disabled={saving} className="px-3 py-1.5 border border-slate-300 text-xs" data-testid="bulk-fill-review-back">Kembali Koreksi</button>
      <button onClick={onConfirm} disabled={saving} className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs uppercase font-semibold" data-testid="bulk-fill-confirm">
        {saving ? "Menyimpan…" : `Konfirmasi Simpan ${list.length} Invoice`}
      </button>
    </div>
  </div>
);

const ApplyBar = ({ count, value, onChange, onApply }) => (
  <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2 text-[11px]" data-testid="bulk-fill-apply-bar">
    <span className="text-slate-600">{count} invoice dipilih · isi satu tanggal ke yang dipilih:</span>
    <DueDatePickerField value={value} onChange={onChange} disabled={!count} testId="bulk-fill-apply-date" />
    <button type="button" disabled={!count || !DATE_RE.test(value)} onClick={onApply}
      className="px-2 py-1 border border-emerald-500 text-emerald-800 disabled:opacity-40 uppercase font-semibold text-[10px]" data-testid="bulk-fill-apply-btn">
      Terapkan ke yang dipilih
    </button>
    <span className="text-slate-400">(hanya mengisi form — belum menyimpan)</span>
  </div>
);

/**
 * Dialog "Isi Due Date" — data LANGSUNG dari antrean kanonis backend
 * (GET /collection/missing-due-date, predikat identik counter & aging).
 * Semua field tanggal kosong saat dibuka; tidak ada default/konstanta.
 */
export default function BulkFillDueDateDialog({ open, onClose, projectId, onSaved, onOpenDetail }) {
  const [queue, setQueue] = useState({ items: [], count: 0, total_outstanding: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [dates, setDates] = useState({});
  const [notes, setNotes] = useState({});
  const [errors, setErrors] = useState({});
  const [stale, setStale] = useState({});
  const [applyDate, setApplyDate] = useState("");
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchQueue = useCallback(async () => {
    const { data } = await api.get("/collection/missing-due-date", { params: projectId ? { project_id: projectId } : {} });
    return data;
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    setSelected({}); setDates({}); setNotes({}); setErrors({}); setStale({}); setApplyDate(""); setReview(null);
    setLoading(true);
    fetchQueue().then(setQueue).catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat antrean."))
      .finally(() => setLoading(false));
  }, [open, fetchQueue]);

  const items = queue.items || [];
  const stateOf = (r) => validate(r, { checked: !!selected[r.entry_id], dd: dates[r.entry_id] || "", stale: stale[r.entry_id] });
  const chosen = items.filter((r) => selected[r.entry_id]);
  // Hanya baris terpilih DENGAN tanggal yang dihitung; checkbox saja ≠ siap simpan.
  const ready = chosen.filter((r) => dates[r.entry_id]);
  const canReview = ready.length > 0 && ready.every((r) => stateOf(r).ok) && !chosen.some((r) => stale[r.entry_id]);
  const toggleRow = (id, v) => {
    setSelected((s) => ({ ...s, [id]: v }));
    if (!v) {
      setDates(({ [id]: _d, ...rest }) => rest);
      setNotes(({ [id]: _n, ...rest }) => rest);
    }
  };

  // Cek ulang versi ke backend sebelum pratinjau — tolak bila data berubah.
  const openReview = async () => {
    setBusy(true);
    try {
      const fresh = await fetchQueue();
      const ver = Object.fromEntries((fresh.items || []).map((r) => [r.entry_id, r.version]));
      const changed = Object.fromEntries(ready.filter((r) => ver[r.entry_id] !== r.version).map((r) => [r.entry_id, true]));
      setStale(changed);
      if (Object.keys(changed).length) { toast.error("Sebagian invoice berubah setelah dialog dibuka — tutup & buka ulang dialog."); return; }
      setReview(ready.map((r) => ({ r, dd: dates[r.entry_id], note: (notes[r.entry_id] || "").trim() })));
    } catch { toast.error("Gagal memeriksa ulang data invoice."); }
    finally { setBusy(false); }
  };

  const confirmSave = async () => {
    setBusy(true);
    setErrors({});
    try {
      const updates = review.map(({ r, dd, note }) => ({ project_id: r.project_id, entry_id: r.entry_id, due_date: dd, expected_version: r.version, reason: note || null }));
      const { data } = await api.post("/active-projects/income/bulk-due-date", { updates, source: "collection_isi_due_date" });
      const errMap = Object.fromEntries((data?.errors || []).filter((e) => e.entry_id).map((e) => [e.entry_id, e.error]));
      setErrors(errMap);
      const ok = (data?.applied_count || 0) + (data?.unchanged_count || 0);
      setReview(null);
      if (ok && !data.error_count) { toast.success(`${ok} invoice tersimpan — counter & aging diperbarui.`); onSaved?.(data); onClose(); return; }
      if (ok) toast.warning(`${ok} tersimpan · ${data.error_count} gagal — periksa status validasi.`);
      else toast.error(`Gagal: ${data?.error_count || 0} invoice tidak tersimpan.`);
      if (ok) { onSaved?.(data); setQueue(await fetchQueue()); setSelected({}); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan due date.");
    } finally { setBusy(false); }
  };

  const openDetail = (r) => {
    if (onOpenDetail) onOpenDetail(r);
    else if (r.detail_link) window.open(r.detail_link, "_blank", "noopener");
  };

  if (!open) return null;
  const fillable = items.filter((r) => r.can_fill);
  const allSelected = fillable.length > 0 && fillable.every((r) => selected[r.entry_id]);
  const applyToSelected = () => setDates((s) => ({ ...s, ...Object.fromEntries(chosen.map((r) => [r.entry_id, applyDate])) }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-fill-due-date-dialog">
      <div className="bg-white border border-slate-300 max-w-7xl w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2">
          <Calendar size={18} className="text-emerald-700" />
          <div className="flex-1">
            <div className="font-bold text-emerald-900 uppercase text-sm">Isi Due Date Invoice Resmi</div>
            <div className="text-[11px] text-emerald-800" data-testid="bulk-fill-queue-summary">
              {queue.count} invoice tanpa due date · outstanding resmi {formatIDR(queue.total_outstanding)} · per {fmtDate(queue.as_of)} (WIB)
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600" data-testid="bulk-fill-close"><X size={18} /></button>
        </div>

        {review ? (
          <ReviewPanel list={review} saving={busy} onBack={() => setReview(null)} onConfirm={confirmSave} />
        ) : (
          <>
            <ApplyBar count={chosen.length} value={applyDate} onChange={setApplyDate} onApply={applyToSelected} />
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="p-6 text-center text-slate-400 text-sm">Memuat antrean…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm" data-testid="bulk-fill-empty">Tidak ada invoice resmi tanpa due date.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr className="text-left text-[10px] uppercase text-slate-600">
                      <th className="px-2 py-2 w-8">
                        <input type="checkbox" checked={allSelected} data-testid="bulk-fill-select-all"
                          onChange={() => { if (allSelected) { setSelected({}); setDates({}); setNotes({}); } else setSelected(Object.fromEntries(fillable.map((r) => [r.entry_id, true]))); }} />
                      </th>
                      <th className="px-2 py-2">Client · Proyek · WO/VO</th>
                      <th className="px-2 py-2">Invoice / Referensi</th>
                      <th className="px-2 py-2">Tgl Invoice</th>
                      <th className="px-2 py-2 text-right">Outstanding</th>
                      <th className="px-2 py-2">Syarat Pembayaran</th>
                      <th className="px-2 py-2">Due Date Saat Ini</th>
                      <th className="px-2 py-2">Due Date Baru · Catatan</th>
                      <th className="px-2 py-2">Status Validasi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <QueueRow key={r.entry_id} r={r} st={stateOf(r)}
                        checked={!!selected[r.entry_id]} dd={dates[r.entry_id] || ""} note={notes[r.entry_id] || ""} err={errors[r.entry_id]}
                        onCheck={(v) => toggleRow(r.entry_id, v)}
                        onDate={(v) => setDates((s) => ({ ...s, [r.entry_id]: v }))}
                        onNote={(v) => setNotes((s) => ({ ...s, [r.entry_id]: v }))}
                        onOpenDetail={openDetail} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
              <div className="text-[10px] text-slate-500 flex-1">
                Due date wajib dipilih manual. Saran hanya muncul bila syarat pembayaran resmi tersedia dan tidak pernah tersimpan tanpa tindakan Anda.
              </div>
              <button onClick={onClose} disabled={busy} className="px-3 py-1.5 border border-slate-300 text-slate-700 text-xs hover:bg-slate-100" data-testid="bulk-fill-cancel">Batal</button>
              <button onClick={openReview} disabled={!canReview || busy}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs uppercase tracking-wider font-semibold" data-testid="bulk-fill-submit">
                Tinjau & Simpan {ready.length} Invoice
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
